import React from "react";
import { useLang, useSettings, cldUrl } from "../lib/providers";
import { Shield, Truck, BadgeCheck, Wallet, Star, Heart } from "lucide-react";

const ICON_MAP = { shield: Shield, truck: Truck, badge: BadgeCheck, wallet: Wallet, star: Star, heart: Heart };

/**
 * Trust component. Two variants:
 *  - default (cards): rendered on the homepage as a full section.
 *  - "lines" (compact): used in cart panel + product detail sheet. One row per card,
 *    showing only the icon/logo + title text (concise — no long body).
 */
export default function Trust({ variant }) {
  const { lang } = useLang();
  const { settings } = useSettings();
  if (!settings.trust_enabled) return null;
  const cards = settings.trust_cards || [];
  if (cards.length === 0) return null;

  if (variant === "lines") {
    return (
      <section className="py-3 bg-neon/5 border-y border-neon/20" data-testid="trust-lines-block">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <ul className="divide-y divide-neon/10">
            {cards.map((c, i) => {
              const Icon = ICON_MAP[c.icon] || Shield;
              const title = lang === "ar" ? (c.title_ar || c.title_fr) : (c.title_fr || c.title_ar);
              return (
                <li key={c.id || i}>
                  <div className="flex items-center gap-3 py-2" data-testid={`trust-line-${i}`}>
                    {c.image_url ? (
                      <img
                        src={cldUrl(c.image_url, { w: 48 })}
                        alt={title}
                        className="w-5 h-5 object-contain shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <Icon className="w-5 h-5 text-neon shrink-0" aria-hidden />
                    )}
                    <p className="text-xs sm:text-sm text-white/85 leading-snug">{title}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    );
  }

  // cards (default) — homepage section
  const title = lang === "ar" ? settings.trust_title_ar : settings.trust_title_fr;
  return (
    <section id="trust" className="py-20 sm:py-28 theme-section-alt" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-5xl max-w-3xl">{title}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {cards.map((c) => {
            const Icon = ICON_MAP[c.icon] || Shield;
            const tt = lang === "ar" ? c.title_ar : c.title_fr;
            const dd = lang === "ar" ? c.text_ar : c.text_fr;
            return (
              <div key={c.id} className="themed-card p-6 transition-colors group">
                {c.image_url ? (
                  <img
                    src={cldUrl(c.image_url, { w: 320 })}
                    alt={tt}
                    style={{ width: `${settings.trust_logos_size_px || 48}px`, height: `${settings.trust_logos_size_px || 48}px` }}
                    className="object-contain mb-4 group-hover:scale-110 transition-transform"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <Icon className="w-7 h-7 text-neon mb-4 group-hover:scale-110 transition-transform" />
                )}
                <h3 className="font-heading font-bold text-lg uppercase tracking-tight">{tt}</h3>
                <p className="text-sm text-white/60 mt-2">{dd}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
